import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  teamsApi,
  type CreateTeamInput,
  type UpdateTeamInput,
  type CreateStatusInput,
  type UpdateStatusInput,
} from '@/lib/api/teams';
import type { CheckMode, MergePolicy } from '@/lib/api/types';

export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  tree: () => [...teamKeys.all, 'tree'] as const,
  epicOwners: () => [...teamKeys.all, 'epicOwners'] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamKeys.details(), id] as const,
  children: (id: string) => [...teamKeys.detail(id), 'children'] as const,
  descendants: (id: string) => [...teamKeys.detail(id), 'descendants'] as const,
  ancestors: (id: string) => [...teamKeys.detail(id), 'ancestors'] as const,
  members: (id: string) => [...teamKeys.detail(id), 'members'] as const,
  statuses: (id: string) => [...teamKeys.detail(id), 'statuses'] as const,
};

export function useTeams() {
  return useQuery({
    queryKey: teamKeys.lists(),
    queryFn: () => teamsApi.list(),
  });
}

export function useTeamTree() {
  return useQuery({
    queryKey: teamKeys.tree(),
    queryFn: () => teamsApi.tree(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEpicOwnerTeams() {
  return useQuery({
    queryKey: teamKeys.epicOwners(),
    queryFn: () => teamsApi.epicOwners(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTeam(id: string) {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => teamsApi.get(id),
    enabled: !!id,
    select: (response) => response.data,
  });
}

export function useTeamChildren(teamId: string) {
  return useQuery({
    queryKey: teamKeys.children(teamId),
    queryFn: () => teamsApi.getChildren(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamDescendants(teamId: string) {
  return useQuery({
    queryKey: teamKeys.descendants(teamId),
    queryFn: () => teamsApi.getDescendants(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamAncestors(teamId: string) {
  return useQuery({
    queryKey: teamKeys.ancestors(teamId),
    queryFn: () => teamsApi.getAncestors(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: () => teamsApi.getMembers(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamStatuses(teamId: string) {
  return useQuery({
    queryKey: teamKeys.statuses(teamId),
    queryFn: () => teamsApi.getStatuses(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTeamInput) => teamsApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateTeamInput) => teamsApi.update(input),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
      queryClient.setQueryData(teamKeys.detail(response.data.id), response.data);
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => teamsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useReparentTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teamsApi.reparent,
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
      queryClient.setQueryData(teamKeys.detail(response.data.id), response.data);
    },
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.addMember(teamId, userId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.removeMember(teamId, userId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) });
    },
  });
}

export function useCreateTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, ...input }: CreateStatusInput & { teamId: string }) =>
      teamsApi.createStatus(teamId, input),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}

export function useUpdateTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      statusId,
      ...input
    }: UpdateStatusInput & { teamId: string; statusId: string }) =>
      teamsApi.updateStatus(teamId, statusId, input),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}

export function useDeleteTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, statusId }: { teamId: string; statusId: string }) =>
      teamsApi.deleteStatus(teamId, statusId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}

/** Connects, changes or disconnects a project's repository. */
export function useSetMergePolicy(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mergePolicy: MergePolicy) => teamsApi.setMergePolicy(teamId, mergePolicy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

/** Sets a project's fast-mode default for runs (ENG-350). */
export function useSetFastMode(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fastMode: boolean) => teamsApi.setFastMode(teamId, fastMode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

/** Turn automatic database migrations on or off for the project (ENG-421). */
export function useSetAutoApplyMigrations(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (autoApplyMigrations: boolean) => teamsApi.setAutoApplyMigrations(teamId, autoApplyMigrations),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

/** Change how often the project's builds run their checks (ENG-395). */
export function useSetCheckMode(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (checkMode: CheckMode) => teamsApi.setCheckMode(teamId, checkMode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

/** Sets (or with null clears) a project's minimum compliance score (ENG-380). */
export function useSetComplianceThreshold(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threshold: number | null) => teamsApi.setComplianceThreshold(teamId, threshold),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

export function useSetProjectRepository(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (url: string | null) => teamsApi.setRepository(teamId, url),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}
